import type { ChainId_L1, ChainLayer } from '@cronos-app/sdk';
import { ChainId } from '@cronos-app/sdk';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { RefetchInterval } from '@ui/configs/ui';
import type { Fraction } from 'bi-fraction';

import type { TokenContext } from '../token/model';
import { Token } from '../token/model';
import { TOKEN_CONFIG_MAP } from '../token/tokenConfig';
import { getTokenAddressByCtx, getTokenAddressKey } from '../token/utils';
import { cdcFetcher } from './fetchers/cdc';
import { stableFetcher } from './fetchers/stable';
import { vethFetcher } from './fetchers/veth';
import { zkcroFetcher } from './fetchers/zkcro';

export const useTokenPrice = ({
  tokenCtx,
  desiredNetwork = ChainId.ETHEREUM,
  ...options
}: Partial<
  UseQueryOptions<{
    usd_price: Fraction;
    usd_price_change_24h: Fraction;
  } | null>
> & {
  tokenCtx?: TokenContext;
  desiredNetwork?: ChainId_L1;
}) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'useTokenPrice',
      desiredNetwork,
      // split fields to avoid re-fetching
      tokenCtx?.layer,
      tokenCtx?.type,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      tokenCtx?.address,
    ],
    queryFn: async () => {
      if (!tokenCtx) throw new Error('tokenCtx is required');
      const res = await tokenFetcher(tokenCtx, desiredNetwork);
      if (!res) return null; // to mark no price for this token
      return res;
    },
    staleTime: RefetchInterval.Slow,
    ...options,
    enabled: tokenCtx !== undefined && options.enabled !== false,
  });
};

export const tokenFetcher = async (
  tokenCtx: TokenContext,
  desiredNetwork: ChainId_L1,
) => {
  if (tokenCtx.type !== Token.Unknown) {
    return basicTokenFetcher(tokenCtx.type, tokenCtx.layer, desiredNetwork);
  } else {
    const addr = getTokenAddressByCtx(desiredNetwork, tokenCtx);
    for (const config of Object.values(TOKEN_CONFIG_MAP[desiredNetwork])) {
      if (
        config[getTokenAddressKey(tokenCtx.layer)].toLowerCase() ===
        addr.toLowerCase()
      )
        return basicTokenFetcher(config.type, tokenCtx.layer, desiredNetwork);
    }
  }
};

/**
 * vUSD is assumed to be stablecoin and supported by `stableFetcher`
 */
const basicTokenFetcher = (
  token: Token,
  layer: ChainLayer,
  desiredNetwork: ChainId_L1 = ChainId.ETHEREUM,
) => {
  if (stableFetcher.support(token, layer, desiredNetwork))
    return stableFetcher.fetch(token, layer, desiredNetwork);
  if (cdcFetcher.support(token, layer, desiredNetwork))
    return cdcFetcher.fetch(token, layer, desiredNetwork);
  if (zkcroFetcher.support(token, layer, desiredNetwork))
    return zkcroFetcher.fetch(token, layer, desiredNetwork);
  if (vethFetcher.support(token, layer, desiredNetwork))
    return vethFetcher.fetch(token, layer, desiredNetwork);
};
